import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, MapPin, User, FolderKanban, Plus, Send, Copy, CheckCircle2 } from "lucide-react";
import { formatDate } from "@/lib/format";
import { PROJECT_STATUS_META } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClientUsersManager } from "@/components/ClientUsersManager";

export default function ClientDetailPage() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get(`/clients/${id}`),
          api.get("/projects", { params: { client_id: id } }),
        ]);
        setClient(c.data); setProjects(p.data);
      } catch (e) { toast.error("Erro ao carregar cliente"); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-40" /><Skeleton className="h-40 rounded-xl" /></div>;
  if (!client) return <p>Cliente não encontrado.</p>;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <div>
        <button onClick={() => navigate("/clients")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-clients">
          <ArrowLeft size={14} /> Clientes
        </button>
      </div>

      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold" data-testid="client-name-heading">{client.name}</h1>
          {client.company && <p className="text-sm text-muted-foreground">{client.company}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={async () => {
            try {
              const r = await api.post(`/clients/${client.id}/resend-invite`);
              setInviteInfo({ name: client.name, email: client.email, invite_url: r.data.invite_url });
              setInviteCopied(false);
              toast.success("Convite reenviado");
            } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
          }} className="gap-2" data-testid="resend-invite-btn">
            <Send size={15} /> Reenviar convite
          </Button>
          <Button onClick={() => navigate(`/projects?client_id=${client.id}&new=1`)} data-testid="new-project-for-client-btn" className="gap-2">
            <Plus size={16} /> Novo projeto
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardContent className="p-6 space-y-3 text-sm">
            <InfoRow icon={Mail} label="E-mail" value={client.email} />
            <InfoRow icon={Phone} label="Telefone" value={client.phone || "-"} />
            <InfoRow icon={Phone} label="WhatsApp" value={client.whatsapp || "-"} />
            <InfoRow icon={MapPin} label="Endereço" value={client.address || "-"} />
            <InfoRow icon={User} label="Responsável" value={client.responsible || "-"} />
            {client.notes && (
              <div className="pt-3 border-t">
                <p className="text-xs text-muted-foreground mb-1">Observações</p>
                <p className="text-sm">{client.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <FolderKanban size={16} className="text-primary" />
              <h3 className="font-heading font-semibold">Projetos deste cliente</h3>
            </div>
            {projects.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhum projeto ainda.</p>
            ) : (
              <ul className="grid sm:grid-cols-2 gap-3">
                {projects.map((p) => {
                  const meta = PROJECT_STATUS_META[p.status] || { label: p.status, color: "#64748B" };
                  return (
                    <li key={p.id}>
                      <Link to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`}
                        className="block p-4 rounded-xl border card-lift bg-card">
                        <p className="font-medium truncate">{p.name}</p>
                        {p.code && <p className="text-xs font-mono text-muted-foreground">{p.code}</p>}
                        <div className="mt-3 flex items-center justify-between">
                          <span className="text-[11px] font-medium rounded-full px-2 py-0.5 border" style={{ color: meta.color, borderColor: `${meta.color}40`, backgroundColor: `${meta.color}15` }}>{meta.label}</span>
                          <span className="text-xs text-muted-foreground">{formatDate(p.due_date)}</span>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <ClientUsersManager clientId={client.id} />

      <Dialog open={!!inviteInfo} onOpenChange={(o) => !o && setInviteInfo(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Convite reenviado</DialogTitle>
          </DialogHeader>
          {inviteInfo && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Um novo link de convite foi gerado para <b className="text-foreground">{inviteInfo.email}</b>.
                O link anterior foi invalidado.
              </p>
              <div className="rounded-lg border bg-muted/40 p-3">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Link (válido por 7 dias)</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs break-all font-mono">{inviteInfo.invite_url}</code>
                  <Button size="sm" variant="outline" onClick={async () => {
                    try { await navigator.clipboard.writeText(inviteInfo.invite_url); setInviteCopied(true); toast.success("Link copiado"); setTimeout(() => setInviteCopied(false), 2500); }
                    catch (_e) { void _e; toast.error("Falha ao copiar"); }
                  }} className="shrink-0 gap-1.5" data-testid="copy-invite-detail-btn">
                    {inviteCopied ? <><CheckCircle2 size={14} className="text-emerald-500" /> Copiado</> : <><Copy size={14} /> Copiar</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setInviteInfo(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={15} className="mt-0.5 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="text-sm break-all">{value}</p>
      </div>
    </div>
  );
}
